/**
 * Composable for managing control bar auto-hide behavior.
 *
 * Automatically hides the control bar after 3 seconds of inactivity
 * when the video is playing. Tracks mouse movement and keyboard focus
 * within the container to prevent hiding while the user interacts, and
 * never hides it while a panel is open.
 *
 * Only keyboard focus (`:focus-visible`) holds the bar. A mouse click also
 * focuses the button it lands on, and treating that as interaction kept the
 * bar up for good after any click — in fullscreen above all, where the
 * fullscreen button keeps the focus and nothing else is left to click.
 *
 * This is the only owner of the bar's visibility: the player's
 * `showControlBar` mirrors `showControlBar` returned here.
 *
 * Pass `pinControls=true` to disable auto-hide entirely — used by demo
 * pages, kiosk modes, or any embed where the bar must remain visible
 * during playback.
 *
 * @param isPlaying - Whether the video is currently playing
 * @param containerRef - Reference to the player container element
 * @param pinControls - If true, the control bar stays visible always
 * @param isPanelOpen - While true, the control bar is not hidden
 * @returns showControlBar ref and showTemporarily function
 *
 * @example
 * const { showControlBar, showTemporarily } = useControlBarVisibility(isPlaying, containerRef)
 */

import { ref, watch, onMounted, onUnmounted, type Ref, type ComputedRef } from 'vue'

const HIDE_DELAY_MS = 3000

export function useControlBarVisibility(
  isPlaying: Ref<boolean> | ComputedRef<boolean>,
  containerRef: Ref<HTMLElement | null>,
  pinControls?: Ref<boolean> | ComputedRef<boolean>,
  isPanelOpen?: Ref<boolean> | ComputedRef<boolean>
) {
  const showControlBar = ref(true)
  const hideTimer = ref<ReturnType<typeof setTimeout> | null>(null)
  const hasFocus = ref(false)

  function isPinned(): boolean {
    return !!pinControls?.value
  }

  function isHeld(): boolean {
    return hasFocus.value || !!isPanelOpen?.value
  }

  function clearHideTimer() {
    if (hideTimer.value) {
      clearTimeout(hideTimer.value)
      hideTimer.value = null
    }
  }

  function scheduleHide() {
    clearHideTimer()

    if (isPinned()) {
      showControlBar.value = true
      return
    }

    if (isPlaying.value && !isHeld()) {
      hideTimer.value = setTimeout(() => {
        if (!isHeld()) {
          showControlBar.value = false
        }
      }, HIDE_DELAY_MS)
    }
  }

  /**
   * Show the control bar temporarily, then auto-hide if playing.
   */
  function showTemporarily() {
    showControlBar.value = true
    scheduleHide()
  }

  function handleMouseMove() {
    showTemporarily()
  }

  function handleMouseLeave() {
    if (isPlaying.value && !hasFocus.value) {
      scheduleHide()
    }
  }

  function handleFocusIn(event: FocusEvent) {
    const target = event.target as Element | null
    if (!target?.matches?.(':focus-visible')) return

    hasFocus.value = true
    showControlBar.value = true
    clearHideTimer()
  }

  function handleFocusOut(event: FocusEvent) {
    const container = containerRef.value
    if (!container) return

    const relatedTarget = event.relatedTarget as Node | null
    if (!relatedTarget || !container.contains(relatedTarget)) {
      if (!hasFocus.value) return
      hasFocus.value = false
      if (isPlaying.value) {
        scheduleHide()
      }
    }
  }

  function bindEvents() {
    const container = containerRef.value
    if (!container) return

    container.addEventListener('mousemove', handleMouseMove)
    container.addEventListener('mouseleave', handleMouseLeave)
    container.addEventListener('focusin', handleFocusIn)
    container.addEventListener('focusout', handleFocusOut)
  }

  function unbindEvents() {
    const container = containerRef.value
    if (!container) return

    container.removeEventListener('mousemove', handleMouseMove)
    container.removeEventListener('mouseleave', handleMouseLeave)
    container.removeEventListener('focusin', handleFocusIn)
    container.removeEventListener('focusout', handleFocusOut)
  }

  // When playback pauses, always show the control bar
  watch(isPlaying, (playing) => {
    if (!playing) {
      showControlBar.value = true
      clearHideTimer()
    } else {
      scheduleHide()
    }
  })

  // React to live changes of the pin flag — coming in via setOptions on
  // the embed SDK / postMessage. Toggling on must immediately show + lock
  // the bar; toggling off must restart the hide timer if playing.
  if (pinControls) {
    watch(pinControls, (pinned) => {
      if (pinned) {
        showControlBar.value = true
        clearHideTimer()
      } else if (isPlaying.value && !isHeld()) {
        scheduleHide()
      }
    })
  }

  // A panel holds the bar while open; closing it restarts the hide timer.
  if (isPanelOpen) {
    watch(isPanelOpen, (open) => {
      if (open) {
        showControlBar.value = true
        clearHideTimer()
      } else {
        scheduleHide()
      }
    })
  }

  watch(containerRef, (newContainer, oldContainer) => {
    if (oldContainer) {
      oldContainer.removeEventListener('mousemove', handleMouseMove)
      oldContainer.removeEventListener('mouseleave', handleMouseLeave)
      oldContainer.removeEventListener('focusin', handleFocusIn)
      oldContainer.removeEventListener('focusout', handleFocusOut)
    }
    if (newContainer) {
      bindEvents()
    }
  })

  onMounted(() => {
    bindEvents()
  })

  onUnmounted(() => {
    clearHideTimer()
    unbindEvents()
  })

  return {
    showControlBar,
    showTemporarily,
  }
}
